'use client'

import { useIsMounted } from '@repo/hooks'
import {
  Button,
  Circle,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@repo/ui'
import { abbreviateAddress } from '@repo/utils'
import { useConnect } from 'wagmi'

import { isE2E_Next } from '../configs/process'
import { useAppKitSSR } from '../hooks/useAppKitSSR'
import { useChains } from '../hooks/useChains'
import { useUserAccount } from '../providers/UserAccountProvider'
import { isSameChain } from '../utils/chain.utils'
import { AddressAvatar } from './AddressAvatar'
import { ChainIcon } from './ChainIcon'

const sizeConfig = {
  sm: { chainButton: 'iconSm', mainButton: 'sm', chainIcon: 'size-3', avatar: 16 },
  md: { chainButton: 'icon', mainButton: 'default', chainIcon: 'size-4', avatar: 16 },
  lg: { chainButton: 'iconLg', mainButton: 'lg', chainIcon: 'size-5', avatar: 20 }
} as const

type ConnectWalletSize = keyof typeof sizeConfig

function MockWalletConnect() {
  const { connectors, connect } = useConnect()
  const { isConnected, userAddress, chain } = useUserAccount()

  const mockConnector = connectors[0]

  if (!mockConnector) {
    return null
  }

  if (isConnected && userAddress) {
    return (
      <Button>
        Connected ({abbreviateAddress(userAddress, 4)}) on {chain?.name}
      </Button>
    )
  }

  return <Button onClick={() => connect({ connector: mockConnector })}>Mock connect</Button>
}

/**
 * Wallet connection control with chain switching and address display.
 * Uses {@link useAppKitSSR} so it can render safely in server-rendered trees
 * where `useAppKit` would throw before AppKit is initialized.
 */
export function ConnectWallet({
  hideChainSelector,
  size = 'sm',
  connectedVariant = 'secondary',
  connectedSuffix,
  ...props
}: Omit<React.ComponentProps<typeof Button>, 'size'> & {
  hideChainSelector?: boolean
  size?: ConnectWalletSize
  /** Variant of the address button once connected. */
  connectedVariant?: React.ComponentProps<typeof Button>['variant']
  /** Optional content rendered after the connected address inside the wallet button. */
  connectedSuffix?: React.ReactNode
}) {
  const config = sizeConfig[size]
  const isMounted = useIsMounted()
  const { isConnected, chain: userChain, userAddress, ensName, switchNetwork } = useUserAccount()
  const { open } = useAppKitSSR()
  const chains = useChains()
  const isSettled = isMounted && isConnected

  if (isE2E_Next) {
    return <MockWalletConnect />
  }

  // Render the address UI only when we actually have an address. A returning
  // user's wallet cookie makes `userAddress` available synchronously (wagmi's
  // `cookieToInitialState`), so their address paints on first render; with no
  // cookie there's nothing to reconnect, so first-paint the plain Connect button
  // rather than a `zeroAddress` placeholder that would flash until reconnect
  // resolves (or never resolves).
  if (!userAddress) {
    return (
      <Button
        type="button"
        color="primary"
        size={config.mainButton}
        onClick={() => open()}
        {...props}
      >
        Connect
      </Button>
    )
  }

  const connectedAddress = userAddress

  return (
    <div className="flex gap-2">
      {!hideChainSelector && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="secondary"
              size={config.chainButton}
              aria-label="Switch Chain Button"
            >
              <ChainIcon chainId={userChain?.id ?? 1} className={config.chainIcon} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" side="bottom" align="end">
            {chains.map(chain => (
              <DropdownMenuItem
                key={chain.id}
                onClick={() => switchNetwork(chain)}
                className="flex cursor-pointer items-center gap-3"
              >
                <div className="flex w-full items-center gap-2">
                  <ChainIcon chainId={chain.id} className="size-4" />
                  <span className="flex-1 text-left">{chain.name}</span>
                  {isSameChain(userChain?.id ?? 1, chain.id) && <Circle size="sm" color="valid" />}
                </div>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      <Button
        type="button"
        size={config.mainButton}
        variant={connectedVariant}
        onClick={() => open()}
        // Gate on the settled `isConnected`, not raw `isReconnecting` — wagmi cycles
        // through transient statuses during mount, which flashed the button
        // enabled → disabled → enabled. The mount gate is repeated on this leaf so
        // the server and its first hydrated render always agree: the provider's own
        // gate is an ancestor that can mount before this subtree hydrates.
        disabled={!isSettled}
        {...props}
        className={cn(!isSettled && 'animate-pulse', props.className)}
      >
        <AddressAvatar address={connectedAddress} size={config.avatar} />
        {ensName ?? abbreviateAddress(connectedAddress)}
        {connectedSuffix}
      </Button>
    </div>
  )
}
